// AI modal dla edytora Lexical Markdown
// Wyodrębniony z lexical_markdown_controller.js
//
// Użycie:
//   import { showLexicalAiModal } from "../src/noe/lexical_ai_modal"
//   const modal = showLexicalAiModal({
//     aiContext: "new_task",
//     aiMode: "creative", // "spelling", "polish", "creative"
//     onUseResponse: (content) => ...,
//     onClose: () => ...
//   })
//   modal.remove() // cleanup

// Klasy pól tekstowych okien edytora Lexical - jawne tło i kolor (tryb ciemny), text-base na mobile (bez auto-zoomu iOS)
export const LEXICAL_MODAL_INPUT_CLASS = "block w-full rounded-md border-0 py-1.5 bg-white text-gray-900 text-base shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"

export function showLexicalAiModal({ aiContext, aiMode = "creative", placeholder, promptLabel, canCustomizePrompt = false, onUseResponse, onClose }) {
  const sparklesSvg = `<svg class="h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
  </svg>`
  const spinnerSvg = `<svg class="animate-spin h-4 w-4 text-white-full" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>`

  // Określ placeholder w zależności od trybu, jeśli nie został podany
  if (!placeholder) {
    const modePlaceholders = {
      spelling: "wklej tekst do poprawienia pisowni i gramatyki",
      polish: "wklej tekst do uładnienia",
      creative: "wpisz co AI ma opisać lub napisać"
    }
    placeholder = modePlaceholders[aiMode] || modePlaceholders.creative
  }

  const modal = document.createElement("div")
  modal.className = "relative z-50"
  modal.innerHTML = `
    <div class="fixed inset-0 bg-gray-500/75 transition-opacity"></div>
    <div class="fixed inset-0 z-10 overflow-y-auto">
      <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
          <h3 class="text-lg font-semibold leading-6 text-gray-900 mb-4 flex items-center gap-2">
            ${sparklesSvg}
            AI
          </h3>
          <div class="space-y-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">${promptLabel || "Prompt"}</label>
              <textarea data-ai-field="text" rows="4" placeholder="${placeholder}"
                class="${LEXICAL_MODAL_INPUT_CLASS} resize-y"></textarea>
            </div>
            <div data-ai-section="response" class="hidden">
              <label class="block text-sm font-medium text-gray-700 mb-1">Odpowiedź AI</label>
              <textarea data-ai-field="response" rows="8"
                class="${LEXICAL_MODAL_INPUT_CLASS} resize-y"></textarea>
            </div>
            <div data-ai-section="error" class="hidden">
              <p class="text-sm text-red-600" data-ai-field="error-text"></p>
            </div>
          </div>
          <div data-ai-section="buttons-prompt" class="mt-4 flex items-center justify-between">
            ${aiContext
? `<button type="button" data-ai-action="toggle-prompt" class="text-xs text-gray-400 hover:text-gray-600 cursor-pointer inline-flex items-center gap-1">
              <svg class="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
              </svg>
              Instrukcje AI
            </button>`
: "<span></span>"}
            <div class="flex gap-2">
              <button type="button" data-ai-action="cancel"
                class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 cursor-pointer">
                Anuluj
              </button>
              <button type="button" data-ai-action="generate"
                class="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white-full shadow-sm hover:bg-blue-500 cursor-pointer inline-flex items-center gap-2">
                Generuj z AI
              </button>
            </div>
          </div>
          <div data-ai-section="system-prompt" class="hidden mt-3">
            <pre class="text-xs text-gray-500 bg-gray-50 rounded-md p-3 max-h-64 overflow-y-auto whitespace-pre-wrap"></pre>
            <div class="flex justify-end mt-1 ${canCustomizePrompt ? "" : "hidden"}">
              <a href="/noe/prompts/system/${aiContext}" title="Dostosuj" class="text-gray-400 hover:text-gray-600">
                <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
              </a>
            </div>
          </div>
          <div data-ai-section="buttons-response" class="mt-4 flex justify-end gap-2 hidden">
            <button type="button" data-ai-action="cancel"
              class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 cursor-pointer">
              Anuluj
            </button>
            <button type="button" data-ai-action="use-response"
              class="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white-full shadow-sm hover:bg-blue-500 cursor-pointer">
              Użyj odpowiedzi AI
            </button>
          </div>
        </div>
      </div>
    </div>
  `

  document.body.appendChild(modal)

  const textInput = modal.querySelector("[data-ai-field='text']")
  const responseSection = modal.querySelector("[data-ai-section='response']")
  const responseInput = modal.querySelector("[data-ai-field='response']")
  const errorSection = modal.querySelector("[data-ai-section='error']")
  const errorText = modal.querySelector("[data-ai-field='error-text']")
  const buttonsPrompt = modal.querySelector("[data-ai-section='buttons-prompt']")
  const buttonsResponse = modal.querySelector("[data-ai-section='buttons-response']")
  const generateBtn = modal.querySelector("[data-ai-action='generate']")

  textInput.focus()

  const csrfToken = document.querySelector("meta[name='csrf-token']").content

  // Toggle system prompt preview (lazy fetch)
  const toggleBtn = modal.querySelector("[data-ai-action='toggle-prompt']")
  const systemPromptSection = modal.querySelector("[data-ai-section='system-prompt']")
  if (toggleBtn && systemPromptSection) {
    let loaded = false
    toggleBtn.addEventListener("click", async () => {
      if (!systemPromptSection.classList.contains("hidden")) {
        systemPromptSection.classList.add("hidden")
        return
      }
      if (!loaded) {
        systemPromptSection.querySelector("pre").textContent = "..."
        systemPromptSection.classList.remove("hidden")
        const res = await fetch(`/noe/prompt/${aiContext}.md`, { credentials: "same-origin" })
        systemPromptSection.querySelector("pre").textContent = res.ok ? await res.text() : "Nie udalo sie zaladowac prompta"
        loaded = true
      } else {
        systemPromptSection.classList.remove("hidden")
      }
    })
  }

  const close = () => {
    modal.remove()
    onClose()
  }

  const showError = (message) => {
    errorText.textContent = message
    errorSection.classList.remove("hidden")
    generateBtn.disabled = false
    generateBtn.innerHTML = "Generuj z AI"
  }

  const generate = async () => {
    const prompt = textInput.value.trim()
    if (!prompt) return

    generateBtn.disabled = true
    generateBtn.innerHTML = `${spinnerSvg} Generowanie...`
    textInput.readOnly = true
    errorSection.classList.add("hidden")

    try {
      // Dla creative używamy aiContext z komponentu (np. "task_editor", "task_comment_editor")
      // Jeśli brak aiContext, nie wysyłamy ai_context (użyje domyślnego)
      const createBody = aiContext ? { ai_context: aiContext } : {}

      const createRes = await fetch("/noe/conversations/create_for_ai.json", {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(createBody),
      })
      const createData = await createRes.json()
      if (!createRes.ok) {
        showError(createData.error || "Błąd tworzenia konwersacji AI")
        textInput.readOnly = false
        return
      }

      const sendRes = await fetch(createData.send_message_url, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken, "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ content: prompt }),
      })
      const sendData = await sendRes.json()
      if (!sendRes.ok) {
        showError(sendData.error || "Błąd generowania odpowiedzi AI")
        textInput.readOnly = false
        return
      }

      responseInput.value = sendData.assistant_message.content
      responseSection.classList.remove("hidden")
      buttonsPrompt.classList.add("hidden")
      buttonsResponse.classList.remove("hidden")
      textInput.readOnly = true
      responseInput.focus()
    } catch (_e) {
      showError("Błąd połączenia z serwerem")
      textInput.readOnly = false
    }
  }

  const useResponse = () => {
    const content = responseInput.value.trim()
    if (!content) { close(); return }
    onUseResponse(content)
    close()
  }

  generateBtn.addEventListener("click", generate)
  modal.querySelector("[data-ai-action='use-response']").addEventListener("click", useResponse)
  modal.querySelectorAll("[data-ai-action='cancel']").forEach(btn => btn.addEventListener("click", close))

  const onKeydown = (e) => {
    if (e.key === "Escape") close()
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); generate() }
  }
  textInput.addEventListener("keydown", onKeydown)
  responseInput.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close()
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); useResponse() }
  })
  modal.querySelector(".fixed.inset-0").addEventListener("click", close)

  return modal
}
